import { Download } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type {
  BusinessDetailsView,
  ProductionReleaseSummary,
  UpdateCustomerSetupResult,
  UpdateDeploymentOperationResult,
} from '@quds/shared';
import { useCan } from '@/auth/AuthContext';
import {
  Badge,
  Button,
  DescriptionList,
  EmptyState,
  FormField,
  Input,
  LoadingState,
  Modal,
  SecretField,
  Select,
} from '@/components/ui';
import {
  deployBusinessUpdate,
  disableBusinessUpdates,
  enableBusinessUpdates,
  getBusinessDetails,
  listUpdateReleases,
  regenerateBusinessUpdateCode,
  revealBusinessUpdateCode,
  setBusinessUpdateChannel,
  setupBusinessUpdates,
} from '@/data';
import { formatDateTime } from '@/lib/format';
import type { ActionModalProps } from '../types';

type Mode =
  | 'overview'
  | 'channel'
  | 'channel-review'
  | 'enable-review'
  | 'disable-review'
  | 'deploy'
  | 'deploy-review'
  | 'deploy-success'
  | 'setup'
  | 'setup-success'
  | 'regenerate-review';

function idempotencyKey(): string {
  return crypto.randomUUID();
}

const CHANNELS = [
  { value: 'stable', label: 'Stable' },
  { value: 'rc', label: 'Release candidate (rc)' },
  { value: 'beta', label: 'Beta' },
];

export function ApplicationUpdateModal({ pharmacy, onClose }: ActionModalProps) {
  const canManage = useCan()('update.manage');
  const [details, setDetails] = useState<BusinessDetailsView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('overview');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [idemKey, setIdemKey] = useState(idempotencyKey);
  const [channel, setChannel] = useState<'stable' | 'rc' | 'beta'>('stable');
  const [releases, setReleases] = useState<ProductionReleaseSummary[]>([]);
  const [releasesLoading, setReleasesLoading] = useState(false);
  const [releaseId, setReleaseId] = useState('');
  const [deployName, setDeployName] = useState('');
  const [updateDeviceId, setUpdateDeviceId] = useState('');
  const [activateDeployment, setActivateDeployment] = useState(true);
  const [deployResult, setDeployResult] = useState<UpdateDeploymentOperationResult | null>(null);
  const [setupChannel, setSetupChannel] = useState<'stable' | 'rc' | 'beta'>('stable');
  const [setupResult, setSetupResult] = useState<UpdateCustomerSetupResult | null>(null);
  const [revealedCode, setRevealedCode] = useState<string | null>(null);
  const [codeMessage, setCodeMessage] = useState<string | null>(null);
  const [codeBusy, setCodeBusy] = useState(false);

  const clearSecrets = () => {
    setRevealedCode(null);
    setCodeMessage(null);
    setSetupResult(null);
  };

  const handleClose = () => {
    clearSecrets();
    onClose();
  };

  const productionAvailable = details?.overview.productionAvailable === true;
  const updatesSection = details?.updates;
  const customer = updatesSection?.customer ?? null;
  const devices = updatesSection?.devices ?? [];
  const clients = updatesSection?.clients ?? [];
  const linked = updatesSection?.availability === 'available' && customer != null;
  const notLinked = updatesSection?.availability === 'not_linked';

  const selectedRelease = useMemo(() => releases.find((r) => r.id === releaseId) ?? null, [releases, releaseId]);

  const reload = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const view = await getBusinessDetails(pharmacy.id);
      setDetails(view);
      const ch = view.updates.customer?.assignedChannel;
      if (ch === 'stable' || ch === 'rc' || ch === 'beta') setChannel(ch);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not load update details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, [pharmacy.id]);

  const loadReleases = async () => {
    setReleasesLoading(true);
    try {
      const res = await listUpdateReleases({ limit: 50, status: 'published' });
      setReleases(res.items);
      if (!releaseId && res.items[0]) setReleaseId(res.items[0].id);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Could not load releases');
    } finally {
      setReleasesLoading(false);
    }
  };

  const submitChannel = async () => {
    setSubmitting(true);
    setFormError(null);
    try {
      await setBusinessUpdateChannel(pharmacy.id, { assignedChannel: channel, idempotencyKey: idemKey });
      setMode('overview');
      await reload();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Channel change failed');
      setMode('channel');
    } finally {
      setSubmitting(false);
    }
  };

  const submitEnable = async () => {
    setSubmitting(true);
    setFormError(null);
    try {
      await enableBusinessUpdates(pharmacy.id, { idempotencyKey: idemKey });
      setMode('overview');
      await reload();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Enable failed');
    } finally {
      setSubmitting(false);
    }
  };

  const submitDisable = async () => {
    setSubmitting(true);
    setFormError(null);
    try {
      await disableBusinessUpdates(pharmacy.id, { idempotencyKey: idemKey });
      setMode('overview');
      await reload();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Disable failed');
    } finally {
      setSubmitting(false);
    }
  };

  const submitDeploy = async () => {
    if (!releaseId || !deployName.trim()) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const res = await deployBusinessUpdate(pharmacy.id, {
        releaseId,
        name: deployName.trim(),
        activate: activateDeployment,
        updateDeviceId: updateDeviceId.trim() || undefined,
        idempotencyKey: idemKey,
      });
      setDeployResult(res);
      setMode('deploy-success');
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Deployment failed');
      setMode('deploy');
    } finally {
      setSubmitting(false);
    }
  };

  const submitSetup = async () => {
    setSubmitting(true);
    setFormError(null);
    try {
      const res = await setupBusinessUpdates(pharmacy.id, {
        pharmacyName: pharmacy.name,
        assignedChannel: setupChannel,
        generateCode: true,
        idempotencyKey: idemKey,
      });
      setSetupResult(res);
      setMode('setup-success');
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Set up updates failed');
      setMode('setup');
    } finally {
      setSubmitting(false);
    }
  };

  const submitRevealCode = async () => {
    setCodeBusy(true);
    setCodeMessage(null);
    try {
      const res = await revealBusinessUpdateCode(pharmacy.id, { idempotencyKey: idempotencyKey() });
      if (res.recoverable && res.updateCode) {
        setRevealedCode(res.updateCode);
      } else {
        setRevealedCode(null);
        setCodeMessage('This Update Code cannot be recovered. Regenerate a new code if needed.');
      }
    } catch (error) {
      setCodeMessage(error instanceof Error ? error.message : 'Reveal failed');
    } finally {
      setCodeBusy(false);
    }
  };

  const submitRegenerateCode = async () => {
    setSubmitting(true);
    setFormError(null);
    setCodeMessage(null);
    try {
      const res = await regenerateBusinessUpdateCode(pharmacy.id, { idempotencyKey: idemKey });
      if (res.updateCode) {
        setRevealedCode(res.updateCode);
        setCodeMessage(null);
      }
      setMode('overview');
      await reload();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Regenerate failed');
      setMode('overview');
    } finally {
      setSubmitting(false);
    }
  };

  if (!loading && !loadError && notLinked && mode !== 'setup' && mode !== 'setup-success') {
    return (
      <Modal
        open
        onClose={handleClose}
        title="Application Update"
        description={pharmacy.name}
        icon={<Download size={18} aria-hidden="true" />}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={handleClose} className="mr-auto">
              Close
            </Button>
            {canManage && (
              <Button
                variant="primary"
                disabled={!productionAvailable}
                onClick={() => {
                  setMode('setup');
                  setIdemKey(idempotencyKey());
                  setFormError(null);
                }}
              >
                Set Up Updates
              </Button>
            )}
          </>
        }
      >
        <EmptyState
          title="Not linked"
          description={updatesSection?.message ?? 'No confirmed updater customer is linked to this business.'}
        />
        {!canManage && <p className="mt-3 text-[13px] text-ink-secondary">View only — your role cannot set up updates.</p>}
      </Modal>
    );
  }

  const title =
    mode === 'setup' || mode === 'setup-success'
      ? mode === 'setup-success'
        ? 'Updates set up'
        : 'Set Up Updates'
      : mode === 'regenerate-review'
        ? 'Regenerate Update Code'
        : mode === 'channel' || mode === 'channel-review'
          ? 'Change update channel'
          : mode === 'enable-review'
            ? 'Enable update customer'
            : mode === 'disable-review'
              ? 'Disable update customer'
              : mode === 'deploy' || mode === 'deploy-review'
                ? 'Targeted deployment'
                : mode === 'deploy-success'
                  ? 'Deployment created'
                  : 'Application Update';

  const customerDisabled = customer?.status === 'disabled' || customer?.status === 'revoked';

  return (
    <Modal
      open
      onClose={handleClose}
      title={title}
      description={pharmacy.name}
      icon={<Download size={18} aria-hidden="true" />}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} className="mr-auto" disabled={submitting}>
            {mode === 'deploy-success' ? 'Close' : 'Cancel'}
          </Button>
          {mode === 'regenerate-review' && (
            <Button variant="primary" onClick={() => void submitRegenerateCode()} disabled={submitting}>
              {submitting ? 'Regenerating…' : 'Confirm regenerate'}
            </Button>
          )}
          {mode === 'overview' && canManage && linked && (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setMode('disable-review');
                  setIdemKey(idempotencyKey());
                  setFormError(null);
                }}
                disabled={!productionAvailable || customerDisabled}
              >
                Disable updates
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setMode('enable-review');
                  setIdemKey(idempotencyKey());
                  setFormError(null);
                }}
                disabled={!productionAvailable || !customerDisabled}
              >
                Enable updates
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setMode('channel');
                  setIdemKey(idempotencyKey());
                  setFormError(null);
                }}
                disabled={!productionAvailable}
              >
                Change channel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setMode('deploy');
                  setIdemKey(idempotencyKey());
                  setDeployName(`${pharmacy.name} deployment`);
                  setFormError(null);
                  void loadReleases();
                }}
                disabled={!productionAvailable}
              >
                Targeted deployment
              </Button>
            </>
          )}
          {mode === 'channel' && (
            <Button variant="primary" onClick={() => setMode('channel-review')} disabled={!customer}>
              Review
            </Button>
          )}
          {mode === 'channel-review' && (
            <Button variant="primary" onClick={() => void submitChannel()} disabled={submitting}>
              {submitting ? 'Saving…' : 'Confirm channel change'}
            </Button>
          )}
          {mode === 'enable-review' && (
            <Button variant="primary" onClick={() => void submitEnable()} disabled={submitting}>
              {submitting ? 'Enabling…' : 'Enable update customer'}
            </Button>
          )}
          {mode === 'disable-review' && (
            <Button variant="primary" onClick={() => void submitDisable()} disabled={submitting}>
              {submitting ? 'Disabling…' : 'Disable update customer'}
            </Button>
          )}
          {mode === 'deploy' && (
            <Button variant="primary" onClick={() => setMode('deploy-review')} disabled={!releaseId || !deployName.trim()}>
              Review
            </Button>
          )}
          {mode === 'deploy-review' && (
            <Button variant="primary" onClick={() => void submitDeploy()} disabled={submitting}>
              {submitting ? 'Creating…' : 'Create deployment'}
            </Button>
          )}
          {mode === 'setup' && (
            <Button variant="primary" onClick={() => void submitSetup()} disabled={submitting || !productionAvailable}>
              {submitting ? 'Setting up…' : 'Create update customer'}
            </Button>
          )}
          {mode === 'setup-success' && (
            <Button
              variant="primary"
              onClick={() => {
                setMode('overview');
                void reload();
              }}
            >
              Done
            </Button>
          )}
        </>
      }
    >
      {loading && <LoadingState className="py-10" label="Loading updater state…" />}
      {loadError && <EmptyState title="Could not load updates" description={loadError} />}

      {!loading && !loadError && mode === 'overview' && linked && customer && (
        <div className="space-y-4">
          {!productionAvailable && <Badge tone="warning">Production temporarily unavailable — writes disabled</Badge>}
          <DescriptionList
            items={[
              { label: 'Update customer', value: customer.pharmacyName },
              { label: 'Status', value: customer.status },
              { label: 'Assigned channel', value: customer.assignedChannel },
              { label: 'Active devices', value: `${customer.activeDeviceCount} / ${customer.deviceCount}` },
              {
                label: 'Last device seen',
                value: customer.lastDeviceSeenAt ? formatDateTime(customer.lastDeviceSeenAt) : '—',
              },
            ]}
          />
          {customer.hasActiveCode && canManage && (
            <SecretField
              label="Update Code"
              value={revealedCode}
              maskedPlaceholder="UPD-••••••••"
              revealed={!!revealedCode}
              revealLoading={codeBusy}
              message={codeMessage}
              disabled={!productionAvailable || customerDisabled}
              onReveal={() => void submitRevealCode()}
              onHide={() => {
                setRevealedCode(null);
                setCodeMessage(null);
              }}
              onClearOnUnmount={clearSecrets}
              onRegenerate={
                productionAvailable && !customerDisabled
                  ? () => {
                      setMode('regenerate-review');
                      setIdemKey(idempotencyKey());
                      setFormError(null);
                    }
                  : undefined
              }
            />
          )}
          {customer.hasActiveCode && !canManage && (
            <SecretField label="Update Code" value={null} maskedPlaceholder="UPD-••••••••" disabled message="View only — your role cannot reveal update codes." />
          )}
          {devices.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-secondary">Updater devices</p>
              <ul className="divide-y divide-line rounded-md border border-line text-sm">
                {devices.map((d) => (
                  <li key={d.id} className="px-4 py-2">
                    <span className="font-medium">{d.nickname ?? d.deviceUuid.slice(0, 12)}</span>
                    <span className="text-ink-secondary">
                      {' '}
                      · v{d.currentVersion} · {d.channel} · {d.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {clients.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-secondary">Update clients</p>
              <ul className="divide-y divide-line rounded-md border border-line text-sm">
                {clients.map((c) => (
                  <li key={c.id} className="px-4 py-2">
                    {c.deviceId} · v{c.currentVersion} · {c.releaseChannel}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="rounded-md border border-line bg-surface-secondary px-3 py-2 text-[13px] text-ink-secondary">
            Upload and publish releases from Deployment Center. Per-device updater channel override is not supported;
            channel is set at update-customer level only.
          </p>
          {!canManage && <p className="text-[13px] text-ink-secondary">View only — your role cannot manage updates.</p>}
        </div>
      )}

      {!loading && mode === 'setup' && (
        <div className="space-y-4">
          {formError && <p className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">{formError}</p>}
          <p className="text-[13px] text-ink-secondary">
            Creates a Production update customer, links it to this business, and shows a one-time Update Code.
          </p>
          <FormField label="Assigned channel" htmlFor="setup-channel">
            <Select
              id="setup-channel"
              value={setupChannel}
              onChange={(e) => setSetupChannel(e.target.value as 'stable' | 'rc' | 'beta')}
              options={CHANNELS}
            />
          </FormField>
        </div>
      )}

      {mode === 'setup-success' && setupResult && (
        <div className="space-y-4">
          <DescriptionList
            items={[
              { label: 'Customer ID', value: setupResult.customerId },
              { label: 'Pharmacy name', value: setupResult.pharmacyName },
              { label: 'Channel', value: setupResult.assignedChannel },
              { label: 'Status', value: setupResult.status },
            ]}
          />
          {setupResult.updateCode ? (
            <SecretField
              label="Update Code"
              value={setupResult.updateCode}
              initiallyRevealed
              notRecoverable
              onClearOnUnmount={clearSecrets}
            />
          ) : (
            <p className="text-[13px] text-ink-secondary">Customer linked. No Update Code was returned.</p>
          )}
        </div>
      )}

      {!loading && (mode === 'channel' || mode === 'channel-review') && customer && (
        <div className="space-y-4">
          {formError && <p className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">{formError}</p>}
          {mode === 'channel' ? (
            <FormField label="Target channel" htmlFor="upd-channel">
              <Select
                id="upd-channel"
                value={channel}
                onChange={(e) => setChannel(e.target.value as 'stable' | 'rc' | 'beta')}
                options={CHANNELS}
              />
            </FormField>
          ) : (
            <DescriptionList
              items={[
                { label: 'Business', value: pharmacy.name },
                { label: 'Update customer', value: customer.pharmacyName },
                { label: 'Current channel', value: customer.assignedChannel },
                { label: 'New channel', value: channel },
              ]}
            />
          )}
        </div>
      )}

      {!loading && mode === 'regenerate-review' && customer && (
        <div className="space-y-4">
          {formError && <p className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">{formError}</p>}
          <DescriptionList
            items={[
              { label: 'Business', value: pharmacy.name },
              { label: 'Update customer', value: customer.pharmacyName },
              {
                label: 'Effect',
                value: 'Issues a new Update Code and invalidates the previous one. Devices must re-register with the new code.',
              },
            ]}
          />
        </div>
      )}

      {!loading && mode === 'enable-review' && customer && (
        <div className="space-y-4">
          {formError && <p className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">{formError}</p>}
          <DescriptionList
            items={[
              { label: 'Business', value: pharmacy.name },
              { label: 'Update customer', value: customer.pharmacyName },
              { label: 'Effect', value: 'Restores updater access for this customer per production semantics (not POS disable).' },
            ]}
          />
        </div>
      )}

      {!loading && mode === 'disable-review' && customer && (
        <div className="space-y-4">
          {formError && <p className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">{formError}</p>}
          <DescriptionList
            items={[
              { label: 'Business', value: pharmacy.name },
              { label: 'Update customer', value: customer.pharmacyName },
              {
                label: 'Effect',
                value: 'Blocks updater/customer update access only. Installed POS continues to run.',
              },
            ]}
          />
        </div>
      )}

      {!loading && (mode === 'deploy' || mode === 'deploy-review') && customer && (
        <div className="space-y-4">
          {formError && <p className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">{formError}</p>}
          {releasesLoading && <LoadingState label="Loading releases…" />}
          {mode === 'deploy' ? (
            <>
              <FormField label="Release" htmlFor="upd-release">
                <Select
                  id="upd-release"
                  value={releaseId}
                  onChange={(e) => setReleaseId(e.target.value)}
                  options={releases.map((r) => ({
                    value: r.id,
                    label: `${r.version} (${r.channel}) — ${r.status}`,
                  }))}
                />
              </FormField>
              <FormField label="Deployment name" htmlFor="upd-deploy-name">
                <Input id="upd-deploy-name" value={deployName} onChange={(e) => setDeployName(e.target.value)} />
              </FormField>
              <FormField label="Target device (optional)" htmlFor="upd-device">
                <Select
                  id="upd-device"
                  value={updateDeviceId}
                  onChange={(e) => setUpdateDeviceId(e.target.value)}
                  options={[
                    { value: '', label: `All devices for customer ${customer.pharmacyName}` },
                    ...devices.map((d) => ({ value: d.id, label: `${d.nickname ?? d.deviceUuid} · v${d.currentVersion}` })),
                  ]}
                />
              </FormField>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={activateDeployment}
                  onChange={(e) => setActivateDeployment(e.target.checked)}
                />
                Activate deployment after create
              </label>
            </>
          ) : (
            <DescriptionList
              items={[
                { label: 'Business', value: pharmacy.name },
                { label: 'Customer', value: customer.pharmacyName },
                { label: 'Release', value: selectedRelease ? `${selectedRelease.version} (${selectedRelease.channel})` : releaseId },
                { label: 'Target', value: updateDeviceId || 'Entire update customer' },
                { label: 'Activate', value: activateDeployment ? 'Yes' : 'No' },
              ]}
            />
          )}
        </div>
      )}

      {mode === 'deploy-success' && deployResult && (
        <DescriptionList
          items={[
            { label: 'Deployment ID', value: deployResult.deploymentId },
            { label: 'Status', value: deployResult.status },
            { label: 'Release', value: deployResult.releaseId },
          ]}
        />
      )}
    </Modal>
  );
}
